import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { track } from '../lib/analytics'
import type { Highlight } from '../lib/types'

interface Props {
  open: boolean
  highlights: Highlight[]
  onClose: () => void
  onScrollToEntry: (entryId: number, selectedText?: string) => void
  onDeleteHighlight: (highlightId: number) => void
  onUpdateNote: (highlightId: number, note: string) => void
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function HighlightsSheet({
  open,
  highlights,
  onClose,
  onScrollToEntry,
  onDeleteHighlight,
  onUpdateNote,
}: Props) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editNote, setEditNote] = useState('')

  useEffect(() => {
    if (!open) { setEditingId(null); setEditNote('') }
  }, [open])

  const handleStartEdit = (h: Highlight) => {
    setEditingId(h.id)
    setEditNote(h.note)
  }

  const handleSaveNote = (highlightId: number) => {
    onUpdateNote(highlightId, editNote)
    setEditingId(null)
    setEditNote('')
  }

  const handleTap = (h: Highlight) => {
    track('highlight_navigated', { highlight_id: h.id })
    onClose()
    setTimeout(() => onScrollToEntry(h.timeline_entry_id, h.selected_text), 200)
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 max-w-lg mx-auto bg-bg rounded-t-2xl border-t border-elevated max-h-[70vh] flex flex-col"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-text-muted/30" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-2">
              <h3 className="font-semibold">
                Highlights ({highlights.length})
              </h3>
              <button
                onClick={onClose}
                className="text-text-muted hover:text-text-primary transition-colors p-1"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* List */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-2">
              {highlights.length === 0 ? (
                <p className="text-text-muted text-sm text-center py-8">
                  No highlights yet. Tap the bookmark icon on any AI response to save it.
                </p>
              ) : (
                highlights.map((h) => (
                  <div
                    key={h.id}
                    className="bg-surface rounded-lg p-3 space-y-1.5"
                  >
                    {/* Context line */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-muted truncate flex-1">
                        {h.user_message_preview}
                      </span>
                      <span className="text-xs text-text-muted shrink-0 ml-2">
                        {timeAgo(h.created_at)}
                      </span>
                    </div>

                    {/* Highlighted text */}
                    <button
                      onClick={() => handleTap(h)}
                      className="text-left w-full"
                    >
                      <p className="text-sm text-text-primary line-clamp-3 border-l-2 border-yellow-500/60 pl-2">
                        {h.selected_text || 'Entire response saved'}
                      </p>
                    </button>

                    {/* Note */}
                    {editingId === h.id ? (
                      <div className="flex gap-2">
                        <input
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNote(h.id) }}
                          placeholder="Add a note..."
                          className="flex-1 bg-elevated text-text-primary text-sm rounded px-2 py-1 outline-none focus:ring-1 focus:ring-accent"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveNote(h.id)}
                          className="text-xs text-accent font-medium px-2"
                        >
                          Save
                        </button>
                      </div>
                    ) : h.note ? (
                      <button
                        onClick={() => handleStartEdit(h)}
                        className="text-xs text-text-secondary italic text-left w-full"
                      >
                        {h.note}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(h)}
                        className="text-xs text-text-muted hover:text-text-secondary transition-colors"
                      >
                        + Add note
                      </button>
                    )}

                    {/* Delete */}
                    <div className="flex justify-end">
                      <button
                        onClick={() => onDeleteHighlight(h.id)}
                        className="text-xs text-text-muted hover:text-error transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
